import { Autocomplete, Card, Grid, Icon, IconButton, MenuItem, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useState, useEffect } from "react";
import { get, post } from "utils/WebService";
import { ESCURO } from "assets/coresTema";

// Regra de carteira (MarcacaoEspecialService no backend): sem carteira = vale para todas (universal); com carteiras, a
// marcação só aparece e só é usada nas fichas e contratos delas. Quem está em equipe com carteira só vincula as suas; as de
// outras equipes que já estavam na marcação ficam (aparecem só como aviso).
export default function MarcacaoEspecialCreateEdit({ id, closeCreateEdit, dataOriginal }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    
    const [formData, setFormData] = useState({
        ccNome: "",
        cvAlvo: 3,
        cvAtivo: true,
        carteiraIds: []
    });

    const [loading, setLoading] = useState(false);
    const [carteirasOpcoes, setCarteirasOpcoes] = useState([]);
    const [carteirasCarregadas, setCarteirasCarregadas] = useState(false);

    useEffect(() => {
        get("/marcacoes/carteiras")
            .then((res) => setCarteirasOpcoes(Array.isArray(res) ? res : []))
            .catch(() => setCarteirasOpcoes([]))
            .finally(() => setCarteirasCarregadas(true));
    }, []);

    // Carteiras da marcação que o usuário não pode escolher (de outras equipes): ficam como estão
    const deOutrasEquipes = !carteirasCarregadas ? [] : (dataOriginal?.carteirasDetalhe || []).filter((c) => !carteirasOpcoes.some((o) => o.id === c.id));

    useEffect(() => {
        if (id && dataOriginal) {
            setFormData({
                ccNome: dataOriginal.ccNome,
                cvAlvo: dataOriginal.cvAlvo,
                cvAtivo: dataOriginal.cvAtivo,
                carteiraIds: dataOriginal.carteiraIds || []
            });
        }
    }, [id, dataOriginal]);

    const handleSave = () => {
        if (!formData.ccNome) {
            alert("Nome é obrigatório");
            return;
        }
        setLoading(true);
        // Só as carteiras que o usuário pode escolher; as de outras equipes o backend mantém
        const payload = { ...formData, carteiraIds: formData.carteiraIds.filter((c) => carteirasOpcoes.some((o) => o.id === c)) };
        const req = id ? post(`/marcacoes/${id}`, payload) : post("/marcacoes", payload);

        req.then(() => {
            setLoading(false);
            closeCreateEdit();
        }).catch((e) => {
            setLoading(false);
            alert(e?.message || "Erro ao salvar marcação");
        });
    };

    return (
        <Card id="marcacao-info" sx={{ background: darkMode ? ESCURO.cartao : "#ffffff" }}>
            <MDBox display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1 }}>
                    <MDTypography variant="h5" color={darkMode ? "white" : "inherit"}>
                        <Icon>arrow_back</Icon>
                    </MDTypography>
                </IconButton>
                <MDTypography variant="h6" color={darkMode ? "white" : "inherit"}>
                    {id ? "Editar" : "Nova"} Marcação Especial
                </MDTypography>
            </MDBox>

            <MDBox px={3} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <MDInput
                            fullWidth
                            variant="standard"
                            label="Nome da Marcação"
                            placeholder="Informe o nome..."
                            value={formData.ccNome}
                            onChange={(e) => setFormData({ ...formData, ccNome: e.target.value })}
                            required
                        />
                    </Grid>
                
                    <Grid item xs={12} sm={3}>
                        <MDInput
                            select
                            fullWidth
                            variant="standard"
                            label="Aplica-se a"
                            value={formData.cvAlvo}
                            onChange={(e) => setFormData({ ...formData, cvAlvo: e.target.value })}
                        >
                            <MenuItem value={1}>Ficha (Devedor)</MenuItem>
                            <MenuItem value={2}>Contrato</MenuItem>
                            <MenuItem value={3}>Ambos</MenuItem>
                        </MDInput>
                    </Grid>

                    <Grid item xs={12}>
                        <Autocomplete
                            multiple
                            options={carteirasOpcoes}
                            getOptionLabel={(o) => o.nome || ""}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            value={carteirasOpcoes.filter((o) => formData.carteiraIds.includes(o.id))}
                            onChange={(_, sel) => setFormData({ ...formData, carteiraIds: sel.map((o) => o.id) })}
                            noOptionsText="Nenhuma carteira"
                            renderInput={(params) => (
                                <MDInput {...params} variant="standard" label="Carteiras"
                                    placeholder={formData.carteiraIds.length ? "" : "Todas as carteiras (universal)"} />
                            )}
                        />
                        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                            Em branco = vale para todas as carteiras (universal). Com carteiras, a marcação só aparece e só pode ser
                            usada nas fichas e contratos delas (ficha, contratos, marcação em bloco, remessa, filtros).
                        </MDTypography>
                        {deOutrasEquipes.length > 0 && (
                            <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                Também vale para carteiras de outras equipes, que continuam: <b>{deOutrasEquipes.map((c) => c.nome).join(", ")}</b>.
                            </MDTypography>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={3} display="flex" alignItems="center" pt={3} pl={1}>
                        <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>Inativo</MDTypography>
                        <Switch 
                            checked={formData.cvAtivo} 
                            onChange={(e) => setFormData({ ...formData, cvAtivo: e.target.checked })} 
                        />
                        <MDTypography variant="button" fontWeight="regular" color={darkMode ? "white" : "text"}>Ativo</MDTypography>
                    </Grid>
                </Grid>

                <MDBox 
                    display="flex" 
                    justifyContent="flex-end" 
                    mt={4} 
                    pt={2}
                    sx={{ borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0" }}
                >
                    <MDButton 
                        variant="outlined" 
                        color="secondary" 
                        onClick={closeCreateEdit} 
                        sx={{ 
                            mr: 2, 
                            color: darkMode ? "#ffffff !important" : undefined, 
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined,
                            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : undefined,
                            "&:hover": {
                                borderColor: darkMode ? "#ffffff !important" : undefined,
                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : undefined
                            }
                        }}
                    >
                        Cancelar
                    </MDButton>
                    <MDButton 
                        variant="contained" 
                        color="info" 
                        onClick={handleSave} 
                        disabled={loading}
                        sx={{
                            backgroundColor: darkMode ? "#007bff !important" : undefined,
                            color: "#ffffff !important",
                            fontWeight: "bold",
                            boxShadow: darkMode ? "0 4px 10px rgba(0, 123, 255, 0.3)" : undefined,
                            "&:hover": {
                                backgroundColor: darkMode ? "#0069d9 !important" : undefined
                            }
                        }}
                    >
                        Salvar
                    </MDButton>
                </MDBox>
            </MDBox>
        </Card>
    );
}
